/* @flow */
import { uniq } from 'ramda';

import { getUserId } from 'src/shared/domain/user/selector/userSelectors';
import { createSlice } from '@reduxjs/toolkit';
import { parseResponse } from 'src/shared/app/base/util/ajaxUtils';
import { patchEntities } from 'src/shared/domain/base/action/entityActions';
import { getBraindatePath } from '@braindate/domain/lib/braindate/util';
import {
  getUserURL,
  getUserId as getUserObjectId,
} from '@braindate/domain/lib/user/util';
import {
  setFetchingStateSlice,
  unsetFetchingStateSlice,
} from 'src/shared/core/util/reducerUtils';
import {
  normalizeExpressionOfGratitude,
  normalizeExpressionOfGratitudes,
} from '@braindate/domain/lib/expressionOfGratitude/util/expressionOfGratitudeNormalization';
import {
  expressionOfGratitudesEndpoint,
  expressionOfGratitudeEndpoint,
  sendExpressionOfGratitudeEndpoint,
} from 'src/shared/app/base/settings/endpointSettings';
import {
  getExpressionOfGratitudeId,
  getExpressionOfGratitudeSender,
  getExpressionOfGratitudeBraindate,
  getExpressionOfGratitudeSendGift,
  getExpressionOfGratitudeRecipient,
  getExpressionOfGratitudeMessage,
  getExpressionOfGratitudeMarkAsReadUrl,
} from '@braindate/domain/lib/expressionOfGratitude/util/expressionOfGratitudeGetters';

// Types
import type { ExpressionOfGratitude } from '@braindate/domain/lib/expressionOfGratitude/type';
import { getEventId } from 'src/shared/domain/event/selector/eventSelectors';

export const expressionOfGratitudeInitialState = {
  isFetching: false,
  isPosting: false,
  fetching: [],
  all: [],
  granted: [],
  received: [],
};
import type { GetState } from 'src/shared/core/type/reduxTypes';

const expressionOfGratitudeSlice = createSlice({
  name: 'expressionOfGratitude',
  initialState: expressionOfGratitudeInitialState,
  reducers: {
    fetchExpressionOfGratitudesRequest: setFetchingStateSlice,
    fetchExpressionOfGratitudesSuccess: (
      state,
      { payload: { all, granted, received } },
    ) => {
      state.all = all;
      state.granted = granted;
      state.received = received;
      unsetFetchingStateSlice(state);
    },
    fetchExpressionOfGratitudesFailure: unsetFetchingStateSlice,
    fetchExpressionOfGratitudeRequest: (state, { payload }) => {
      state.fetching = [...state.fetching, payload];
    },
    fetchExpressionOfGratitudeSuccess: (
      state,
      {
        payload: { isGranted, id },
      }: { payload: { isGranted: boolean, id: number } },
    ) => {
      state.all = uniq([...state.fetching, id]);

      if (isGranted) {
        state.granted = uniq([...state.granted, id]);
      } else {
        state.received = uniq([...state.received, id]);
      }
      unsetFetchingStateSlice(state);
    },
    fetchExpressionOfGratitudeFailure: (state, { payload }) => {
      state.fetching = state.fetching.filter(
        (item: number) => item !== payload,
      );
    },
    postExpressionOfGratitudeRequest: (state) => {
      state.isPosting = true;
    },
    postExpressionOfGratitudeSuccess: (state, { payload: result }) => {
      state.all = [...state.all, result];
      state.granted = [...state.granted, result];
      state.isPosting = false;
    },
    postExpressionOfGratitudeFailure: (state) => {
      state.isPosting = false;
    },
  },
});

export const {
  fetchExpressionOfGratitudesRequest,
  fetchExpressionOfGratitudesSuccess,
  fetchExpressionOfGratitudesFailure,
  fetchExpressionOfGratitudeRequest,
  fetchExpressionOfGratitudeSuccess,
  fetchExpressionOfGratitudeFailure,
  postExpressionOfGratitudeRequest,
  postExpressionOfGratitudeFailure,
  postExpressionOfGratitudeSuccess,
} = expressionOfGratitudeSlice.actions;

export function fetchExpressionOfGratitude(props: { id: number }) {
  const { id } = props;

  return (dispatch: any, getState: GetState, { get }: { get: Function }) => {
    const userId = getUserId(getState());
    const eventId = getEventId(getState());

    dispatch(fetchExpressionOfGratitudeRequest());

    return get(expressionOfGratitudeEndpoint(eventId, id))
      .then(parseResponse)
      .then((json: ExpressionOfGratitude) => {
        const normalizedData = normalizeExpressionOfGratitude(json);
        const { entities, result } = normalizedData;

        const author = getExpressionOfGratitudeSender(json);
        const isGranted = !!author && getUserObjectId(author) === userId;

        dispatch(patchEntities(entities));
        dispatch(
          fetchExpressionOfGratitudeSuccess({
            isGranted,
            id: result,
          }),
        );
      })
      .catch((e) => {
        dispatch(fetchExpressionOfGratitudeFailure(id));

        throw e;
      });
  };
}

export function fetchExpressionOfGratitudes() {
  return (dispatch: any, getState: GetState, { get }: { get: Function }) => {
    const userId = getUserId(getState());
    const eventId = getEventId(getState());
    dispatch(fetchExpressionOfGratitudesRequest());

    return get(expressionOfGratitudesEndpoint(eventId))
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeExpressionOfGratitudes(json);
        const { entities, result } = normalizedData;

        const granted = json
          .filter(
            (expression) =>
              getUserObjectId(getExpressionOfGratitudeSender(expression)) ===
              userId,
          )
          .map((expression) => getExpressionOfGratitudeId(expression));
        const received = json
          .filter(
            (expression) =>
              getUserObjectId(getExpressionOfGratitudeSender(expression)) !==
              userId,
          )
          .map((expression) => getExpressionOfGratitudeId(expression));

        dispatch(patchEntities(entities));
        dispatch(
          fetchExpressionOfGratitudesSuccess({
            all: result,
            granted,
            received,
          }),
        );
      })
      .catch((e) => {
        dispatch(fetchExpressionOfGratitudesFailure(e));

        throw e;
      });
  };
}

export function postExpressionOfGratitude(newExpressionOfGratitude: Object) {
  return (dispatch: any, _: GetState, { post }: { post: Function }) => {
    const message = getExpressionOfGratitudeMessage(newExpressionOfGratitude);
    const forBraindate = getExpressionOfGratitudeBraindate(
      newExpressionOfGratitude,
    );
    const recipient = getExpressionOfGratitudeRecipient(
      newExpressionOfGratitude,
    );
    const sendGift = getExpressionOfGratitudeSendGift(newExpressionOfGratitude);

    dispatch(postExpressionOfGratitudeRequest());
    return post(sendExpressionOfGratitudeEndpoint(), {
      message,
      braindate: getBraindatePath(forBraindate),
      recipient: getUserURL(recipient),
      send_gift: !!sendGift,
    })
      .then(parseResponse)
      .then((json) => {
        const normalizedData = normalizeExpressionOfGratitude(json);
        const { entities, result } = normalizedData;

        dispatch(patchEntities(entities));
        dispatch(postExpressionOfGratitudeSuccess(result));
      })
      .catch((e) => {
        dispatch(postExpressionOfGratitudeFailure(e));

        throw e;
      });
  };
}

export function markExpressionOfGratitudeAsRead(
  expressionOfGratitude: ExpressionOfGratitude,
) {
  return (dispatch: any, _: GetState, { post }: { post: Function }) => {
    const path = getExpressionOfGratitudeMarkAsReadUrl(expressionOfGratitude);

    return post(path)
      .then(parseResponse)
      .catch((e) => {
        dispatch(postExpressionOfGratitudeFailure(e));

        throw e;
      });
  };
}

export default expressionOfGratitudeSlice.reducer;
